import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Col, Flex, Form, Input, notification, Row, Space, Spin, Tooltip, Typography, Upload } from 'antd'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CommonModal from 'src/app/shared/components/modal/common-modal'
import {
  colorBgError,
  colorBgErrorBold,
  colorBorder,
  colorText,
  colorTextSelected,
  colorTextSubtle,
  colorTextSubtlest,
} from 'src/app/themes/primaryThemeDefault'
import { generateValidationSchema, useFormRules } from './validation'
import { ErrorMessage } from '@hookform/error-message'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import DownloadWhiteIcon from 'src/assets/images/download-white.png'
import './index.scss'
import { UploadFileIcon, UploadSuccessFileIcon } from 'src/assets/icons'
import SelectCpn from './SelectCpn'
import { LoadingOutlined } from '@ant-design/icons'
import { fileExtension, useDownloadFileTemplate, validateFile } from './useUploadFile'
import { getClientListName } from 'src/app/shared/services/ClientSetupService'
import {
  getAccountListByClientList,
  saveUploadACHRules,
  uploadXlsxFileACHExceptionRule,
} from 'src/app/shared/services/ACHExceptionRuleService'
import { debounce } from 'lodash'
import saveAs from 'file-saver'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { getListErrorUploadACH } from 'src/app/shared/services/BackgroundService'
import CustomSelect from 'src/app/shared/components/common/select'
const Dragger = Upload.Dragger

const ACHUploadTemplateModal = forwardRef((props: any) => {
  const { t } = useTranslation()
  const { isOpen, onClose, onViewErrorResults, setErrorRows, setIsResetView } = props

  const [isLoading, setIsLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [accountLoading, setAccountLoading] = useState<boolean>(false)
  const [clientLoading, setClientLoading] = useState<boolean>(false)
  const [accountList, setAccountList] = useState<any[]>([])
  const [clientList, setClientList] = useState<any[]>([])
  const [isFileContainsInvalidData, setIsFileContainsInvalidData] = useState<boolean>(false)
  const rules = useFormRules()
  const [isLoadingUploadLocal, setIsLoadingUploadLocal] = useState<boolean>(false)
  const { downloadFileACHExceptionRules } = useDownloadFileTemplate()
  const defaultValues = {
    clientNames: null,
    accountNames: [],
    allAccounts: true,
    uploadFile: null,
  }

  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    getValues,
    clearErrors,
    trigger,
    setValue,
    setError,
    reset,
    resetField,
  } = useForm<any>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(generateValidationSchema(t)),
  })

  const handleClose = () => {
    reset()
    onClose()
    setIsFileContainsInvalidData(false)
    setIsLoading(false)
  }

  const uploadFile = useWatch({
    control,
    name: 'uploadFile',
  })
  const isSaveButtonDisabled =
    !!Object.keys(errors)?.length ||
    (!getValues('clientNames') && !Object.keys(dirtyFields)?.length) ||
    isFileContainsInvalidData ||
    !getValues('uploadFile') ||
    (!getValues('accountNames')?.length && !getValues('allAccounts'))

  const handleBeforeUpload = async (file: any) => {
    setIsFileContainsInvalidData(false)

    const s30sTimeout = setTimeout(() => {
      if (!file) {
        setError('uploadFile', {
          type: 'validation',
          message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.FILE_UPLOAD_TIMEOUT}`),
        })
        return false
      }
    }, 30000)

    const errorMes = validateFile(file, t)
    if (file) {
      setValue('uploadFile', file)
      clearTimeout(s30sTimeout)
    }
    if (errorMes) {
      setError('uploadFile', { type: 'validation', message: errorMes })
      return false
    } else {
      clearErrors('uploadFile')
    }
  }

  const handleOnchangeClient = (e: any, clear: boolean) => {
    const curentClients = e
    if (clear) {
      setValue('accountNames', [])
      clearErrors('accountNames')
    }

    const payload = {
      clientIds: curentClients.map((item: any) => (item instanceof Object ? item.value : item)),
    }
    setAccountLoading(true)
    getAccountListByClientList(payload).then((res: any) => {
      if (res?.data) {
        const listAccount = [].concat.apply(
          [],
          res?.data?.map((item: any) => item.accountNames),
        )

        setAccountList(listAccount?.map((item: any) => ({ label: item?.accountName, value: item?.accountId })))
        setAccountLoading(false)
      }
    })
  }

  const debounceSearchStateFn = useCallback(debounce(handleOnchangeClient, 500), [])
  const handleDownloadTemplate = async () => {
    try {
      setLoading(true)
      const res = await downloadFileACHExceptionRules()
      if (res) {
        const data = await (await fetch(res?.value?.path)).arrayBuffer()
        const dataBlob = new Blob([new Uint8Array(data)])
        saveAs(dataBlob, res?.value?.fileName)
        setLoading(false)
      }
    } catch (e) {
      console.log('ERROR', e)
      setLoading(false)
    }
  }

  const onSubmit = async (data: any) => {
    setIsLoading(true)
    const payload = {
      clientId: data.clientNames ?? '',
      // NOTE: GBBM-422
      file: data.uploadFile,
      // accountIds: data.accountNames?.map((item: any) => (item instanceof Object ? item.value : item)) || [],
      // allAccount: data.allAccounts,
    }

    const formData: any = new FormData()
    formData.append('file', data.uploadFile.originFileObj)
    // NOTE: GBBM-422
    // payload.clientIds?.forEach((item: any) => {
    //   formData.append('clientIds', item)
    // })
    // payload.accountIds?.forEach((item: any) => {
    //   formData.append('accountIds', item)
    // })
    // formData.append('allAccount', payload.allAccount)
    console.log('PAYLOAD: ', payload)
    uploadXlsxFileACHExceptionRule(formData)
      .then((res: any) => {
        if (res?.data) {
          saveUploadACHRules({ ...payload, file: res?.data?.fileName, filePath: res?.data?.filePath })
            .then((resp: any) => {
              if (resp?.dataImportProcessLogId) {
                getListErrorUploadACH(resp?.dataImportProcessLogId).then((response: any) => {
                  if (response?.data) {
                    setErrorRows?.(response?.data)
                    setIsLoading(false)
                    setIsFileContainsInvalidData(true)

                    if (response && response?.data?.processedItems?.length > 0) {
                      setIsResetView?.(true)
                    }
                  }
                })
              } else {
                notification.success({
                  message: t(
                    `SystemMessages:${SYSTEM_MESSAGE_IDS.ACH_EXCEPTION_RULE_CREATED_FROM_UPLOAD_SUCCESSFULLY}`,
                  ),
                  closeIcon: null,
                })
                setIsResetView?.(true)
                handleClose()
              }
            })
            .catch((e: any) => {
              if (e?.errors?.dataImpPrcLogId[0]) {
                getListErrorUploadACH(e?.errors?.dataImpPrcLogId[0]).then((res: any) => {
                  if (res?.data) {
                    setErrorRows && setErrorRows(res?.data)
                  }
                })
              }
              setIsLoading(false)
              setIsFileContainsInvalidData(true)
            })
        }
      })
      .catch((error: any) => {
        setIsLoading(false)
        if (error?.errors) {
          const MsgCodeList = [].concat.apply([], Object.values(error?.errors))

          MsgCodeList.forEach((item: any) => {
            setError('uploadFile', {
              message: t(`SystemMessages:${item}`),
            })
          })
        }
      })
  }

  useEffect(() => {
    setClientLoading(true)
    getClientListName().then((res: any) => {
      if (res?.data) {
        setClientList(
          res?.data?.map((item: any) => ({
            label: item.clientName,
            value: item.id,
            note: item.clientNotes,
          })),
        )

        if (res?.data?.length === 1) {
          setValue('clientNames', res?.data[0]?.id)
          debounceSearchStateFn([res?.data[0]?.id], true)
        }
      }
    })
    setClientLoading(false)
  }, [isOpen])

  return (
    <CommonModal
      title={t('ACHExceptionRule:uploadModal.title')}
      width={952}
      open={isOpen}
      size='middle'
      onClose={handleClose}
      centered
      footer={[
        <Flex className='button-group-modal' key='group-btn' justify='flex-end' gap={24}>
          <Button
            key='cancel-btn'
            style={{
              fontWeight: 600,
              width: '102px',
              height: '32px',
              border: `1px solid ${colorBorder}`,
            }}
            onClick={handleClose}
          >
            <Typography.Text>{t('ACHExceptionRule:button.cancel')}</Typography.Text>
          </Button>
          <Button
            key='save-btn'
            className='save-button'
            style={{
              fontWeight: 600,
              width: '102px',
              height: '32px',
              border: `1px solid ${colorBorder}`,
            }}
            type='primary'
            disabled={isSaveButtonDisabled || isLoadingUploadLocal || isLoading}
            onClick={handleSubmit((data) => onSubmit(data))}
            loading={isLoading}
          >
            {t('common:save_btn')}
          </Button>
        </Flex>,
      ]}
    >
      <Form
        name='exception-rule-modal-form'
        className='form-container exception-rule-modal-form'
        layout='vertical'
        style={{ padding: 0 }}
      >
        <Form.Item
          label={t('ACHExceptionRule:client.selectClient')}
          className='isRequired'
          style={{ marginBottom: 16 }}
        >
          <Controller
            name='clientNames'
            control={control}
            render={({ field }) => (
              <CustomSelect
                {...field}
                style={{ width: '100%' }}
                maxTagCount='responsive'
                options={clientLoading ? [] : clientList}
                status={errors?.clientNames ? 'error' : ''}
                placeholder={t('ACHExceptionRule:client.placeholder')}
                maxTagPlaceholder={(omittedValues) => (
                  <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                    <span>+{omittedValues?.length}</span>
                  </Tooltip>
                )}
                defaultActiveFirstOption
                loading={clientLoading}
                showSearch={true}
                onChange={(e) => {
                  field.onChange(e)
                  debounceSearchStateFn([e], true)
                  clearErrors('uploadFile')
                  resetField('uploadFile')
                  setIsFileContainsInvalidData(false)
                }}
              />
            )}
          />
          <Typography className='error-message'>
            <ErrorMessage errors={errors} name='clientNames' />
          </Typography>
        </Form.Item>
        <Form.Item label={t('ACHExceptionRule:uploadModal.downloadTemplate')} style={{ marginBottom: 16 }}>
          <Controller
            name='downloadTemplate'
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                status={fieldState.error ? 'error' : ''}
                className='sample-file-upload ant-input-view-only download-tmp'
                placeholder={t('ACHExceptionRule:uploadModal.uploadFile.exampleFileName')}
                suffix={
                  loading ? (
                    <Spin indicator={<LoadingOutlined spin />} style={{ width: 24, height: 24 }} />
                  ) : (
                    <img src={DownloadWhiteIcon} alt='download ach icon' onClick={handleDownloadTemplate} />
                  )
                }
                readOnly
              />
            )}
          />
          <Typography className='error-message'>
            <ErrorMessage errors={errors} name='downloadTemplate' />
          </Typography>
        </Form.Item>

        <Flex vertical>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label={t('ACHExceptionRule:uploadModal.selectFile')}
                className='isRequired'
                style={{ marginBottom: 0 }}
              >
                {uploadFile ? (
                  <Controller
                    name='uploadFile'
                    control={control}
                    rules={rules.uploadACHFile}
                    render={({ field }) => (
                      <Flex vertical>
                        <Flex
                          style={{
                            minHeight: 66,
                            border: `1px solid ${
                              isFileContainsInvalidData || errors.uploadFile ? colorBgErrorBold : colorBorder
                            }`,
                            borderRadius: 12,
                            backgroundColor: isFileContainsInvalidData || errors.uploadFile ? colorBgError : '',
                          }}
                        >
                          {isLoadingUploadLocal ? (
                            <Flex justify={'center'} align={'center'} style={{ width: '100%' }}>
                              <Spin indicator={<LoadingOutlined spin />} />
                              <Typography.Text style={{ fontSize: 14, marginLeft: 8 }}>
                                {' '}
                                {t('ACHExceptionRule:uploadModal.uploadFile.uploading')}
                              </Typography.Text>
                            </Flex>
                          ) : (
                            <Flex
                              justify={'space-between'}
                              align={'center'}
                              style={{
                                width: '100%',
                                minHeight: 66,
                                padding: '0px 16px',
                              }}
                            >
                              <Flex justify={'flex-start'} align={'center'} style={{ maxWidth: 740 }}>
                                <UploadSuccessFileIcon style={{ minWidth: 40 }} />
                                <Flex vertical style={{ padding: 12 }}>
                                  <Typography.Text>{uploadFile && uploadFile.name}</Typography.Text>
                                  <Typography.Text
                                    style={{
                                      fontSize: 14,
                                      color: colorTextSubtlest,
                                    }}
                                  >
                                    {uploadFile && (uploadFile.size * 0.001).toFixed(2)}KB
                                  </Typography.Text>
                                </Flex>
                              </Flex>

                              <Upload
                                {...field}
                                showUploadList={false}
                                accept={fileExtension.join(',')}
                                beforeUpload={handleBeforeUpload}
                                onChange={(info) => {
                                  setIsLoadingUploadLocal(true)

                                  setTimeout(() => {
                                    setIsLoadingUploadLocal(false)
                                    setValue('uploadFile', info.file)
                                  }, 1000)
                                }}
                              >
                                <Typography.Text
                                  style={{
                                    color: colorTextSelected,
                                    textDecoration: 'underline',
                                    fontSize: 14,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {t('ACHExceptionRule:uploadModal.uploadFile.chooseAnotherFile')}
                                </Typography.Text>
                              </Upload>
                            </Flex>
                          )}
                        </Flex>
                        <Col span={24} style={{ marginTop: 12 }}>
                          <Flex justify='space-between' align='center'>
                            <Typography.Text
                              style={{
                                fontSize: 14,
                                color: colorTextSubtle,
                              }}
                            >
                              {t('ACHExceptionRule:uploadModal.uploadFile.fileSupport')}
                            </Typography.Text>
                            {isFileContainsInvalidData && (
                              <Typography className='error-message'>
                                <Typography.Text style={{ color: colorBgErrorBold }}>
                                  {t('ACHExceptionRule:uploadModal.uploadFile.fileContainsInvalidDate')}
                                </Typography.Text>
                                <Typography.Text
                                  style={{
                                    color: '#004B91',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    fontWeight: 400,
                                  }}
                                  onClick={() => onViewErrorResults && onViewErrorResults()}
                                >
                                  {t('ACHExceptionRule:uploadModal.uploadFile.viewErrorData')}
                                </Typography.Text>
                              </Typography>
                            )}
                          </Flex>
                        </Col>
                      </Flex>
                    )}
                  />
                ) : (
                  <Controller
                    name='uploadFile'
                    control={control}
                    rules={rules.uploadACHFile}
                    render={({ field }) => (
                      <Row>
                        <Col span={24} style={{ height: 128 }}>
                          <Dragger
                            {...field}
                            showUploadList={false}
                            accept={fileExtension.join(',')}
                            beforeUpload={handleBeforeUpload}
                            onChange={(info) => {
                              setIsLoadingUploadLocal(true)

                              setTimeout(() => {
                                setIsLoadingUploadLocal(false)
                                setValue('uploadFile', info.file)
                              }, 1000)
                            }}
                          >
                            <Flex style={{ height: 48 }} justify={'center'}>
                              {isLoadingUploadLocal ? (
                                <Flex justify={'center'} align={'center'}>
                                  <Spin indicator={<LoadingOutlined spin />} />
                                  <Typography.Text style={{ fontSize: 14, marginLeft: 8 }}>
                                    {t('ACHExceptionRule:uploadModal.uploadFile.uploading')}
                                  </Typography.Text>
                                </Flex>
                              ) : (
                                <Space>
                                  <UploadFileIcon />
                                  <Typography.Text
                                    style={{
                                      marginLeft: 12,
                                      fontSize: 14,
                                      fontWeight: 400,
                                      color: colorText,
                                    }}
                                  >
                                    {t('ACHExceptionRule:uploadModal.uploadFile.dragAndDrop')}
                                  </Typography.Text>
                                  <Typography.Text
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 400,
                                      color: colorTextSelected,
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    {t('ACHExceptionRule:uploadModal.uploadFile.chooseFile')}
                                  </Typography.Text>
                                </Space>
                              )}
                            </Flex>
                          </Dragger>
                        </Col>
                        <Col span={24} style={{ marginTop: 12 }}>
                          <Flex justify='space-between' align='center'>
                            <Typography.Text
                              style={{
                                fontSize: 14,
                                color: colorTextSubtle,
                              }}
                            >
                              {t('ACHExceptionRule:uploadModal.uploadFile.fileSupport')}
                            </Typography.Text>
                            {isFileContainsInvalidData && (
                              <Typography className='error-message'>
                                <Typography.Text style={{ color: colorBgErrorBold }}>
                                  {t('ACHExceptionRule:uploadModal.uploadFile.fileContainsInvalidDate')}
                                </Typography.Text>
                                <Typography.Text
                                  style={{
                                    color: '#004B91',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    fontWeight: 400,
                                  }}
                                  onClick={() => onViewErrorResults && onViewErrorResults()}
                                >
                                  {t('ACHExceptionRule:uploadModal.uploadFile.viewErrorData')}
                                </Typography.Text>
                              </Typography>
                            )}
                          </Flex>
                        </Col>
                      </Row>
                    )}
                  />
                )}
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='uploadFile' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>
        </Flex>
      </Form>
    </CommonModal>
  )
})

export default ACHUploadTemplateModal
